import React from 'react';
import { motion } from 'framer-motion';
import { Box, Container, Typography, Grid, Paper, Card, CardContent, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SecurityIcon from '@mui/icons-material/Security';

const About = () => {
  const values = [
    'Integrity & Transparency',
    'Customer Obsession',
    'Innovation in Manufacturing',
    'Safety First Culture',
    'Sustainable Practices'
  ];

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" fontWeight="bold">
          About AYDA INDIA
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.9, mt: 2 }}>
          Pioneering Precision Manufacturing Solutions
        </Typography>
      </Box>

      {/* Detailed Introduction with Stats */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="overline" color="primary" fontWeight="bold" letterSpacing={1.5}>
              OUR STORY
            </Typography>
            <Typography variant="h3" gutterBottom fontWeight="900" sx={{ mb: 4, color: '#1a237e' }}>
              Engineering the Future <br /> Since 2008
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.8 }}>
              AYDA INDUSTRIES PVT LTD was established with a singular vision: to bridge the gap between complex engineering designs and tangible, high-precision components.
              Based in the heart of India's manufacturing hub, we specialize in advanced Vertical Machining Center (VMC) technology,
              delivering components that power critical sectors like Aerospace, Automotive, and General Engineering.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.8 }}>
              Our journey is defined by a relentless pursuit of PERFECTION. We understand that in our line of work, microns matter.
              That's why we have invested heavily in state-of-the-art machinery and a skilled workforce that treats every project with the attention it deserves.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 4, bgcolor: '#e3f2fd', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="h2" fontWeight="bold" color="primary">15+</Typography>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">YEARS OF EXCELLENCE</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 4, bgcolor: '#fff3e0', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="h2" fontWeight="bold" color="warning.main">50+</Typography>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">SKILLED EXPERTS</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={3} sx={{ p: 4, bgcolor: '#fafafa', borderRadius: 4, borderLeft: '8px solid #0d47a1', display: 'flex', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h5" gutterBottom fontStyle="italic" color="text.primary">
                      "We don't just manufacture parts; we craft reliability. Our customers trust us as their strategic partner."
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold" align="right" sx={{ mt: 2, color: 'primary.main' }}>
                      - MD, AYDA INDUSTRIES
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {/* Vision & Mission */}
      <Box sx={{ bgcolor: '#eeeeee', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Card sx={{ height: '100%', p: 2, width: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VisibilityIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h4" fontWeight="bold">Our Vision</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    To be a globally recognized leader in precision manufacturing, known for innovation, quality, and ethical business practices.
                    We aim to set new benchmarks in the machining industry by integrating smart technology with expert craftsmanship.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Card sx={{ height: '100%', p: 2, width: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrackChangesIcon fontSize="large" color="secondary" sx={{ mr: 2 }} />
                    <Typography variant="h4" fontWeight="bold">Our Mission</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    To deliver precision-engineered components that exceed customer expectations through continuous improvement,
                    technological advancement, and a commitment to on-time delivery. We strive to create value for our clients, employees, and stakeholders.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Core Values */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight="bold" sx={{ mb: 6 }}>
          Our Core Values
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {values.map((label, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper elevation={2} sx={{ p: 4, display: 'flex', alignItems: 'center', borderRadius: 2, height: '100%' }}>
                <StarIcon color="warning" sx={{ mr: 2 }} />
                <Typography variant="h6">{label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Infrastructure Detail */}
      <Box sx={{ bgcolor: '#1a237e', color: 'white', py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="#4fc3f7" fontWeight="bold" letterSpacing={1.5}>
                CAPABILITIES
              </Typography>
              <Typography variant="h3" gutterBottom fontWeight="bold">
                World-Class Infrastructure
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', opacity: 0.85, lineHeight: 1.8, mb: 4 }}>
                Our 10,000 sq. ft. facility is designed for efficiency and safety. We believe that identifying the right tool for the job is half the battle won.
              </Typography>

              <Box sx={{ borderLeft: '1px solid rgba(255,255,255,0.2)', pl: 4 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#4fc3f7', mb: 1 }}>Precision VMCs</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>High-speed spindles (10k RPM+) for superior surface finish.</Typography>

                <Typography variant="h5" fontWeight="bold" sx={{ color: '#4fc3f7', mb: 1 }}>CMM Lab</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>Temperature-controlled environment for micron-level validation.</Typography>

                <Typography variant="h5" fontWeight="bold" sx={{ color: '#4fc3f7', mb: 1 }}>CAD/CAM Stations</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>Latest Mastercam & SolidWorks licenses for seamless design-to-part workflow.</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <img src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=600&q=80" alt="Factory Floor 1" style={{ width: '100%', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }} />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ mt: { sm: 4 } }}>
                  <img src="https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?auto=format&fit=crop&w=600&q=80" alt="Factory Floor 2" style={{ width: '100%', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Leadership Team (NEW) */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Box textAlign="center" mb={8}>
          <Typography variant="overline" color="primary" fontWeight="bold">OUR LEADERSHIP</Typography>
          <Typography variant="h3" fontWeight="bold">Meet the Experts</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Driven by passion and united by a goal to deliver excellence.
          </Typography>
        </Box>
        <Grid container spacing={4} justifyContent="center">
          {[
            { name: 'Rajesh Kumar', role: 'Managing Director', img: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=400&q=80' },
            { name: 'Anita Desai', role: 'Head of Operations', img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=80' },
            { name: 'Suresh Reddy', role: 'Technical Lead', img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=400&q=80' }
          ].map((leader, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Paper
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                elevation={2}
                sx={{ p: 0, borderRadius: 4, overflow: 'hidden', textAlign: 'center', height: '100%' }}
              >
                <Box sx={{ height: 300, overflow: 'hidden' }}>
                  <img src={leader.img} alt={leader.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight="bold">{leader.name}</Typography>
                  <Typography variant="subtitle1" color="primary">{leader.role}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* NEW SECTION: Manufacturing Philosophy */}
      <Box sx={{ bgcolor: '#fff3e0', py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" color="warning.dark" fontWeight="bold">OUR PHILOSOPHY</Typography>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#e65100' }}>
                Precision with Purpose
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                At AYDA, we believe that every micron counts. Our manufacturing philosophy is built on the pillars of **Accuracy, Speed, and Reliability**.
                We don't just machine parts; we engineer confidence.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                By integrating Industry 4.0 standards, we ensure real-time monitoring of our production lines, minimizing downtime and maximizing output quality.
                Our commitment extends beyond the shop floor – we build lasting relationships through transparent communication and unwavering support.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"
                alt="Philosophy"
                sx={{ width: '100%', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Corporate Timeline (ENHANCED) */}
      <Box sx={{ bgcolor: '#fafafa', py: 12 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={10}>
            <Typography variant="overline" color="primary" fontWeight="bold">OUR JOURNEY</Typography>
            <Typography variant="h3" fontWeight="bold">Milestones of Excellence</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}>
              From humble beginnings to a global manufacturing partner.
            </Typography>
          </Box>
          <Box sx={{ position: 'relative' }}>
            {/* Center Line */}
            <Box sx={{
              position: 'absolute',
              left: { xs: 20, md: '50%' },
              top: 0,
              bottom: 0,
              width: 4,
              bgcolor: '#e0e0e0',
              transform: { md: 'translateX(-2px)' }
            }} />

            {[
              { year: '2008', title: 'Examples Founded', desc: 'Started with 2 Conventional Lathes in a small 500 sq. ft. workshop, serving local textile industries.' },
              { year: '2012', title: 'First CNC VMC', desc: 'Transitioned to CNC technology to meet growing automotive demand, installing our first Haas VF-2.' },
              { year: '2015', title: 'Expansion Phase I', desc: 'Moved to a larger facility. Added 3 more VMCs and a CNC Turning center to the fleet.' },
              { year: '2016', title: 'ISO 9001 Certification', desc: 'Formalized quality management systems (QMS) to ensure consistency and compliance for export markets.' },
              { year: '2019', title: 'Aerospace Entry', desc: 'Successfully audited by a Tier-1 Aerospace supplier, beginning our journey in high-precision aerospace components.' },
              { year: '2020', title: 'Plant Expansion Phase II', desc: 'Inaugurated a 10,000 sq ft state-of-the-art facility with a dedicated temperature-controlled QC lab.' },
              { year: '2024', title: 'Global Export Hub', desc: 'Established direct export channels to European and North American markets, solidifying our global footprint.' }
            ].map((item, i) => (
              <Box key={i} sx={{ mb: 6, position: 'relative', display: 'flex', justifyContent: { xs: 'flex-start', md: i % 2 === 0 ? 'flex-end' : 'flex-start' } }}>
                {/* Dot */}
                <Box sx={{
                  position: 'absolute',
                  left: { xs: 20, md: '50%' },
                  top: 20,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  border: '4px solid white',
                  boxShadow: 2,
                  zIndex: 2,
                  transform: 'translateX(-8px)'
                }} />

                <Paper
                  component={motion.div}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  elevation={3}
                  sx={{
                    p: 4,
                    width: { xs: 'calc(100% - 60px)', md: '45%' },
                    ml: { xs: 8, md: 0 },
                    borderRadius: 3,
                    position: 'relative',
                    '&:hover': { transform: 'translateY(-5px)', transition: '0.3s' }
                  }}
                >
                  <Typography variant="h2" fontWeight="900" color="rgba(0,0,0,0.05)" sx={{ position: 'absolute', top: 10, right: 20, fontSize: '4rem' }}>{item.year}</Typography>
                  <Chip label={item.year} color="primary" size="small" sx={{ mb: 1, fontWeight: 'bold' }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.6}>{item.desc}</Typography>
                </Paper>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Certifications Section */}
      <Box sx={{ py: 12, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography variant="overline" color="secondary" fontWeight="bold">COMPLIANCE & QUALITY</Typography>
            <Typography variant="h3" fontWeight="bold">Certifications & Standards</Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={4}>
              <Paper elevation={4} sx={{ p: 4, textAlign: 'center', height: '100%', borderTop: '4px solid #43a047' }}>
                <VerifiedIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>ISO 9001:2015</Typography>
                <Typography variant="body2" color="text.secondary">Certified Quality Management System ensuring consistent product quality and customer satisfaction.</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={4} sx={{ p: 4, textAlign: 'center', height: '100%', borderTop: '4px solid #0288d1' }}>
                <PrivacyTipIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>AS9100D Compliant</Typography>
                <Typography variant="body2" color="text.secondary">Adhering to aerospace quality management standards for aviation, space, and defense organizations.</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={4} sx={{ p: 4, textAlign: 'center', height: '100%', borderTop: '4px solid #f57c00' }}>
                <SecurityIcon color="warning" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>EHS Compliance</Typography>
                <Typography variant="body2" color="text.secondary">Committed to Environment, Health, and Safety standards (ISO 14001 / ISO 45001 guidelines).</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
